import type { ReportCategory, ReportStatus, ReportTargetType } from "./types";

const SNAPSHOT_KEYS = [
  "body_md",
  "body",
  "display_name",
  "user_id",
  "message_id",
  "room_id",
  "files",
  "attachments",
] as const;

export const CATEGORY_LABEL: Record<ReportCategory, string> = {
  harassment: "嫌がらせ",
  spam: "迷惑行為",
  inappropriate: "不適切な内容",
  other: "その他",
};

export const STATUS_LABEL: Record<ReportStatus, string> = {
  open: "未対応",
  resolved: "対応済み",
  dismissed: "却下",
};

export const TARGET_LABEL: Record<ReportTargetType, string> = {
  message: "メッセージ",
  user: "ユーザー",
};

export function formatTimestamp(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return new Date(value).toLocaleString();
}

function asPlain(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

function isUnsafeKey(key: string): boolean {
  return /(_url|Url)$/.test(key) || /(url|href|src|secret|token|password|r2_key|blob_key|authorization|email)/i.test(key);
}

function pickPlain(record: Record<string, unknown>, keys: readonly string[]): string {
  for (const key of keys) {
    const text = asPlain(record[key]);
    if (text) return text;
  }
  return "";
}

function formatFileIdentity(value: unknown, index: number): string {
  if (!value || typeof value !== "object" || Array.isArray(value)) return "";
  const rec = value as Record<string, unknown>;
  const bits: string[] = [];
  const id = pickPlain(rec, ["id", "file_id"]);
  const filename = pickPlain(rec, ["filename", "name"]);
  const mime = pickPlain(rec, ["mime", "mime_type"]);
  const size = pickPlain(rec, ["size"]);
  if (id) bits.push(`識別子 ${id}`);
  if (filename) bits.push(`ファイル名 ${filename}`);
  if (mime) bits.push(`形式 ${mime}`);
  if (size) bits.push(`サイズ ${size}`);
  return bits.length ? `添付 ${index + 1}: ${bits.join(" · ")}` : "";
}

function formatFileList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    const single = formatFileIdentity(value, 0);
    return single ? [single] : [];
  }
  return value.map((item, index) => formatFileIdentity(item, index)).filter(Boolean);
}

/** Flatten a stored snapshot into plain text. Never treat the value as HTML. Never print URLs. */
export function formatSnapshot(snapshot: unknown): string {
  if (snapshot == null || snapshot === "") return "";
  if (typeof snapshot !== "object") return asPlain(snapshot);
  const record = snapshot as Record<string, unknown>;
  const lines: string[] = [];
  const seen = new Set<string>();
  for (const key of SNAPSHOT_KEYS) {
    if (record[key] == null || record[key] === "") continue;
    seen.add(key);
    if (key === "files" || key === "attachments") {
      lines.push(...formatFileList(record[key]));
      continue;
    }
    const text = asPlain(record[key]);
    if (text) lines.push(`${key}: ${text}`);
  }
  for (const [key, value] of Object.entries(record)) {
    if (seen.has(key) || value == null || value === "" || isUnsafeKey(key)) continue;
    if (Array.isArray(value)) {
      const files = formatFileList(value);
      if (files.length) {
        lines.push(...files);
        continue;
      }
      continue;
    }
    if (typeof value === "object") continue;
    const text = asPlain(value);
    if (text) lines.push(`${key}: ${text}`);
  }
  return lines.join("\n");
}

export function describeReportTarget(report: {
  target_type: ReportTargetType;
  target_id: string;
  target_user_id?: string | null;
  room_id?: string | null;
}): string {
  const kind = TARGET_LABEL[report.target_type] ?? report.target_type;
  const parts = [`${kind} ${report.target_id}`];
  if (report.target_user_id) parts.push(`ユーザー ${report.target_user_id}`);
  if (report.room_id) parts.push(`ルーム ${report.room_id}`);
  return parts.join(" / ");
}
